import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../../components/AnimatedCol";
import { getClassName, getValue } from "../../../../components/Converter";
import PlusIcon from "../../../../components/Icons/PlusIcon";
import ConvertUnit from "../../../../components/Math/Conversion/ConvertUnit";
import { ImperialUnits, MetricUnits } from "../../../../components/Math/Units/AllUnits";
import Kilopound from "../../../../components/Math/Units/Imperial/Kilopound";
import KiloNewton from "../../../../components/Math/Units/Metric/KiloNewton";
import { useGetDesignCombinationQuery } from "../../../../services/API/titanApi";
import {
  selectDeadLoadFactor,
  selectDesignCombinationDeadLoad,
  selectDesignCombinationLiveLoad,
  selectDesignCombinationName,
  selectLiveLoadFactor,
  setDesignCombinationDeadLoad,
  setDesignCombinationFactors,
  setDesignCombinationLiveLoad,
  setDesignCombinationName,
  setTotalDesignLoad,
} from "../../../../store/slices/aashtoSlice";
import { selectIsImperial, selectIsMetric } from "../../../../store/slices/settingsSlice";

function DesignCombination({ show, showModal }) {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const designCombinationName = useSelector(selectDesignCombinationName);
  const designCombinationDeadLoad = useSelector(selectDesignCombinationDeadLoad);
  const designCombinationDeadLoadFactor = useSelector(selectDeadLoadFactor);
  const designCombinationLiveLoad = useSelector(selectDesignCombinationLiveLoad);
  const designCombinationLiveLoadFactor = useSelector(selectLiveLoadFactor);

  const isImperial = useSelector(selectIsImperial);
  const isMetric = useSelector(selectIsMetric);

  let unitType = isMetric ? MetricUnits.KiloNewton : isImperial ? ImperialUnits.Kilopound : null;

  const { data: strength } = useGetDesignCombinationQuery();

  function handleDesignCombination(selectedValue) {
    let selectedDesignCombination;
    if (selectedValue === "Custom") {
      selectedDesignCombination = {
        name: "Custom",
      };
      dispatch(
        setDesignCombinationFactors({
          deadLoadFactor: 1,
          liveLoadFactor: 1,
        })
      );
    } else {
      selectedDesignCombination = strength?.find((element) => element.name == selectedValue);
      dispatch(
        setDesignCombinationFactors({
          deadLoadFactor: selectedDesignCombination?.deadLoadFactor,
          liveLoadFactor: selectedDesignCombination?.liveLoadFactor,
        })
      );
    }

    dispatch(setDesignCombinationName(selectedDesignCombination?.name));
  }

  useEffect(() => {
    let totalDesignLoad =
      Number(designCombinationDeadLoad.metric) * Number(designCombinationDeadLoadFactor) +
      Number(designCombinationLiveLoad.metric) * Number(designCombinationLiveLoadFactor);

    dispatch(
      setTotalDesignLoad({
        value: totalDesignLoad,
        unit: MetricUnits.KiloNewton,
      })
    );
  }, [
    designCombinationName,
    designCombinationDeadLoad,
    designCombinationDeadLoadFactor,
    designCombinationLiveLoad,
    designCombinationLiveLoadFactor,
  ]);

  const metricUnit = <KiloNewton />;
  const imperialUnit = <Kilopound />;

  return (
    <div className="highlight_box">
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p>{t("aashto.inner.Design Combination")}</p>
          </div>
          <div className="col-1"></div>
          <div className="col-2">
            <select
              className="change-input"
              onChange={(e) => {
                handleDesignCombination(e.target.value);
              }}
            >
              <option value={0}>{t("select")}</option>
              {strength?.map((el, index) => {
                return (
                  <option
                    key={`DesignCombination-${index}`}
                    value={el?.name}
                    selected={designCombinationName && el?.name == designCombinationName}
                  >
                    {t(["aashto.inner." + el?.name])}
                  </option>
                );
              })}
              <option
                key={`DesignCombination-${999}`}
                value={"Custom"}
                selected={designCombinationName && "Custom" === designCombinationName}
              >
                {t("custom")}
              </option>
            </select>
          </div>

          <div className="col-1">
            <div onClick={() => showModal(!show)}>
              <PlusIcon />
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>

      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p>{t("aashto.inner.Dead Load")}</p>
          </div>
          <div className="col-1"></div>
          <div className="col-2">
            <input
              value={getValue(designCombinationDeadLoad.current)}
              onChange={(e) => {
                dispatch(
                  setDesignCombinationDeadLoad({
                    value: e.target.value,
                    unit: unitType,
                  })
                );
              }}
              className={getClassName(designCombinationDeadLoad.current)}
              type="text"
            />
          </div>
          <div className="col-4">
            <span className="angepassteBemessunglast--input--formula">
              <span>
                [<ConvertUnit Metric={metricUnit} Imperial={imperialUnit} />]
              </span>
              <span>x</span>
              &nbsp;
              {designCombinationName === "Custom" ? (
                <input
                  value={getValue(designCombinationDeadLoadFactor)}
                  onChange={(e) => {
                    dispatch(
                      setDesignCombinationFactors({
                        deadLoadFactor: e.target.value,
                        liveLoadFactor: designCombinationLiveLoadFactor,
                      })
                    );
                  }}
                  className={"vlera-Editable"}
                  type="text"
                />
              ) : (
                <span className="vlera">{designCombinationDeadLoadFactor}</span>
              )}
            </span>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
      <AnimatedCol>
        <div className="row">
          <div className="col-4">
            <p>{t("aashto.inner.Live Load")}</p>
          </div>
          <div className="col-1"></div>
          <div className="col-2">
            <input
              value={getValue(designCombinationLiveLoad.current)}
              onChange={(e) => {
                dispatch(
                  setDesignCombinationLiveLoad({
                    value: e.target.value,
                    unit: unitType,
                  })
                );
              }}
              className={getClassName(designCombinationLiveLoad.current)}
              type="text"
            />
          </div>
          <div className="col-4">
            <span className="angepassteBemessunglast--input--formula">
              <span>
                [<ConvertUnit Metric={metricUnit} Imperial={imperialUnit} />]
              </span>
              <span>x</span>
              &nbsp;
              {designCombinationName === "Custom" ? (
                <input
                  value={getValue(designCombinationLiveLoadFactor)}
                  onChange={(e) => {
                    dispatch(
                      setDesignCombinationFactors({
                        deadLoadFactor: designCombinationDeadLoadFactor,
                        liveLoadFactor: e.target.value,
                      })
                    );
                  }}
                  className={"vlera-Editable"}
                  type="text"
                />
              ) : (
                <span className="vlera">{designCombinationLiveLoadFactor}</span>
              )}
            </span>
          </div>
        </div>
        <div className="col-md-8">
          <hr />
        </div>
      </AnimatedCol>
    </div>
  );
}

export default DesignCombination;
